import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../config";
import {
  DEFAULT_APP_TIMEZONE,
  formatAppDate,
  formatAppDateTime,
  formatAppDistanceToNow,
  formatAppTime,
  getAppDisplayTimezone,
  setAppDisplayTimezone,
} from "../utils/appDateTime";

const AppTimezoneContext = createContext({
  timezone: DEFAULT_APP_TIMEZONE,
  loading: true,
  refreshTimezone: async () => {},
  formatDateTime: formatAppDateTime,
  formatDate: formatAppDate,
  formatTime: formatAppTime,
});

export function AppTimezoneProvider({ children }) {
  const [timezone, setTimezone] = useState(getAppDisplayTimezone());
  const [loading, setLoading] = useState(true);

  const refreshTimezone = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/config/app-timezone`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      const next = data?.data?.timezone || DEFAULT_APP_TIMEZONE;
      setAppDisplayTimezone(next);
      setTimezone(getAppDisplayTimezone());
    } catch (err) {
      console.warn("Failed to load app timezone", err);
      setAppDisplayTimezone(DEFAULT_APP_TIMEZONE);
      setTimezone(getAppDisplayTimezone());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTimezone();
  }, [refreshTimezone]);

  const value = useMemo(
    () => ({
      timezone,
      loading,
      refreshTimezone,
      formatDateTime: (value, options) =>
        formatAppDateTime(value, { ...options, timeZone: timezone }),
      formatDate: (value, options) =>
        formatAppDate(value, { ...options, timeZone: timezone }),
      formatTime: (value, options) =>
        formatAppTime(value, { ...options, timeZone: timezone }),
      formatDistanceToNow: (value, options) =>
        formatAppDistanceToNow(value, options),
    }),
    [timezone, loading, refreshTimezone],
  );

  return (
    <AppTimezoneContext.Provider value={value}>
      {children}
    </AppTimezoneContext.Provider>
  );
}

export function useAppTimezone() {
  return useContext(AppTimezoneContext);
}
